/**
 * RBAC — the single role→capability matrix, shared by api (built pkg), web
 * (source alias), and mcp so the three surfaces can never drift.
 *
 * Roles live in the infras-local `app_roles` table (email → role); the staff-auth
 * JWT `root` claim only BOOTSTRAPS the first root row. Unknown users default to
 * `new_member` (onboarding surface only: docs + Get Started + PAT mint).
 *
 * Secret levels keep the DB names from 010_key_levels ('critical'/'restricted'/
 * 'normal'); the UI displays them as L0/L1/L3 (org language — L2 reserved).
 * Visibility: root sees all, maintainer L1+L3, engineer L3, new_member none.
 */

export type AppRole = 'root' | 'maintainer' | 'engineer' | 'new_member';

export type SecretLevel = 'critical' | 'restricted' | 'normal';

export const APP_ROLES: AppRole[] = ['root', 'maintainer', 'engineer', 'new_member'];

export const SECRET_LEVELS: SecretLevel[] = ['critical', 'restricted', 'normal'];

/** Display badges — org language. L2 is reserved/unused. */
export const SECRET_LEVEL_LABELS: Record<SecretLevel, string> = {
  critical: 'L0',
  restricted: 'L1',
  normal: 'L3',
};

/** Which secret-inventory rows (names/metadata) each role may see. */
export const ROLE_ALLOWED_LEVELS: Record<AppRole, SecretLevel[]> = {
  root: ['critical', 'restricted', 'normal'],
  maintainer: ['restricted', 'normal'],
  engineer: ['normal'],
  new_member: [],
};

export const canSeeInventory = (role: AppRole): boolean => role !== 'new_member';

export const canEditSecretLevels = (role: AppRole): boolean =>
  role === 'root' || role === 'maintainer';

export const canAssignRoles = (role: AppRole): boolean => role === 'root';

/**
 * Curate the help-centre URL on a customer error code. root|maintainer — the same
 * bar as every other curated field, and for the same reason here: a doc_url is
 * PUBLISHED to customers on the emitted event, so a bad one is a bad link in a
 * customer's error message, not just a wrong row in an internal table.
 */
export const canEditErrorCodeDocs = (role: AppRole): boolean =>
  role === 'root' || role === 'maintainer';

/**
 * Remove entries from a live Atlas IP access list.
 *
 * Root only, and narrower than the rest of that page on purpose: `reconcile` and
 * `refreshLiveCount` are root|maintainer because they only ever ADD or re-read,
 * while this is the one control in the app that deletes from a third party — and a
 * wrong deletion takes a production cluster off its database. Mirrors
 * `@RequireRole('root')` on the `pruneAtlasOrphans` mutation; the API is the
 * enforcement, this is so we don't offer a button that would only return 403.
 */
export const canPruneAtlas = (role: AppRole): boolean => role === 'root';

/**
 * Cascade-remove an agent and the Atlas addresses it put on each project's list.
 *
 * The SAME rule as `canPruneAtlas`, deliberately expressed as its own name rather than
 * reused: it is the same third-party delete against the same production access list,
 * reached from the other direction (agent-first instead of address-first), so the person
 * who may prune may cascade. Named separately so that if the two ever need to diverge,
 * the divergence is a code change with a reason attached rather than a silent widening of
 * whichever one happened to be shared.
 *
 * Mirrors `@RequireRole('root')` on the `removeAgents` mutation. The API is the
 * enforcement; this exists so we do not render a button that could only return 403.
 */
export const canRemoveAgents = (role: AppRole): boolean => role === 'root';

/**
 * Repo ownership curation.
 *   - Owner (accountable stakeholder) is assigned only by app root|maintainer — the
 *     "find the owner first" step is ours.
 *   - Maintainer (incident contact) is assigned by app root|maintainer OR by the
 *     repo's own Owner (delegation — the owner adds their maintainer afterwards).
 * Lower roles never self-serve: the only elevated path for a non-root/maintainer is
 * already being that repo's Owner.
 */
export const canAssignRepoOwner = (role: AppRole): boolean =>
  role === 'root' || role === 'maintainer';

export const canAssignRepoMaintainer = (role: AppRole, callerIsRepoOwner: boolean): boolean =>
  canAssignRepoOwner(role) || callerIsRepoOwner;

/**
 * Curate a repo's CLASSIFICATION (product line / kind / service). root|maintainer — the same
 * privileged tier that assigns owners. The ONE canonical rule: the dashboard, the MCP tool and
 * the resolver all call this, so "who may classify" can never drift between surfaces again
 * (it used to: the dashboard was root-only while MCP allowed maintainer). Decided 2026-08.
 */
export const canClassifyRepo = (role: AppRole): boolean =>
  role === 'root' || role === 'maintainer';

/**
 * Engineers ("who do I pull in to work on this") are staffed by the same people
 * who staff Maintainers — root|maintainer, or the repo's own Owner delegating.
 * Deliberately NOT also the repo's existing Maintainers: the roster would then be
 * able to grow itself with neither an Owner nor an admin in the loop.
 */
export const canAssignRepoEngineer = canAssignRepoMaintainer;

/**
 * Access tasks: see/decide the full pending queue + manage the default access
 * assignees and question templates. Assignee-scoped actions (deciding an access
 * task you're assigned to) don't need this.
 */
export const canReviewAccessRequests = (role: AppRole): boolean =>
  role === 'root' || role === 'maintainer';

/**
 * Decide (approve/deny) a request you filed YOURSELF. Everyone else is refused —
 * four-eyes is the default and stays the default.
 *
 * Root is exempt because for root the guard was never a control, only a detour:
 * approving RECORDS a grant that happened out-of-band, and root already holds the
 * power to make that grant, to appoint a reviewer, or to change roles. So the rule
 * only forced root to bother a second person to rubber-stamp their own note.
 * Root self-deciding is a self-documented grant ("a note to myself").
 *
 * The trade is auditability, so it is NOT silent: a self-decision stamps
 * `selfDecided` on the timeline event and is called out in the UI + Slack, which
 * makes it greppable and obvious to a reviewer that no second pair of eyes saw it.
 */
export const canDecideOwnAccessRequest = (role: AppRole): boolean => role === 'root';

/**
 * Cancel a request you did NOT file. Cancelling is withdrawal, not a decision —
 * the requester owns it — but root needs it for housekeeping (a requester who
 * left, or an abandoned thread nobody will decide). Deliberately root-only:
 * maintainers decide (approve/deny), they don't withdraw someone else's ask.
 */
export const canCancelAnyAccessRequest = (role: AppRole): boolean => role === 'root';

/**
 * Task labels admin (`/settings/labels`): create, rename, recolor, archive and
 * hard-delete never-used labels. Applying a label to a task is NOT this — any
 * engineer+ may label (see canWorkBoard). See docs/tasks.md.
 */
export const canManageLabels = (role: AppRole): boolean =>
  role === 'root' || role === 'maintainer';

/**
 * The weekly task board: see it, apply/remove labels, drag cards between the
 * workflow columns. Every engineer and up — deliberately the same line as
 * canSeeInventory (no "team" concept). new_member is refused by route AND API.
 */
export const canWorkBoard = (role: AppRole): boolean => canSeeInventory(role);

/**
 * Agent control plane: draft/plan/cancel commands against live clusters and see the
 * command queue. Mirrors canReviewAccessRequests.
 */
export const canControlAgents = (role: AppRole): boolean =>
  role === 'root' || role === 'maintainer';

/**
 * APPROVAL is data-dependent, so it cannot be expressed as a decorator: a maintainer
 * may approve read-only ops anywhere and mutating ops on non-production clusters, but a
 * MUTATING op on a PRODUCTION cluster is root-only. This is the role-layer half of
 * "never auto-apply/deploy anything production-related"; the other half is the
 * draft→approve split plus the plan digest.
 */
export const canApproveAgentCommand = (
  role: AppRole,
  cmd: { mutating: boolean; production: boolean },
): boolean => role === 'root' || (role === 'maintainer' && !(cmd.mutating && cmd.production));

/**
 * Command output can contain pod logs (env dumps, tokens in stack traces), so it is
 * gated at the same tier as secret levels and is NOT part of the command object.
 */
export const canReadCommandOutput = (role: AppRole): boolean =>
  role === 'root' || role === 'maintainer';

/**
 * MCP tools a new_member may call — enough to verify their PAT + client wiring
 * (catalog = deploy-recipe docs; search is level-filtered so secret docs never
 * surface for an empty allowed-levels set) PLUS the access-request tools: new
 * members are exactly the people who need to request access. Requests are
 * visible org-wide: every role can list and read every request
 * (listAccessRequests, assertRequestVisible). Role limits who can decide one.
 */
export const NEW_MEMBER_MCP_TOOLS = [
  'search',
  'list_catalog',
  'get_catalog',
  'create_access_request',
  'list_access_requests',
  'get_access_request',
  'comment_access_request',
  'edit_access_request',
  'cancel_access_request',
  // Plain tasks: the core enforces the web's new_member rules (own plain tasks only; no labels).
  'create_task',
  'list_tasks',
  'get_task',
  'comment_task',
  'update_task',
  'close_task',
  'reopen_task',
];

/**
 * MCP tools that WRITE curated data and are gated to root|maintainer (mirrors
 * canEditSecretLevels). Everything else stays read-only-for-all / recommend-only.
 * These writes are hidden from an engineer's tools/list (the handlers also refuse
 * them defensively): `set_key_note` sets a config key's markdown description;
 * `update_repo` curates a repo's product line, service and Owner/Maintainer;
 * `bulk_assign_owner` assigns one person across every repo matching a filter; and
 * `set_alert_selector` declares a service's Prometheus alert-label selector.
 *
 * NOTE `update_repo` is gated STRICTER here than the underlying rule: a repo's own
 * Owner may assign that repo's Maintainer (`canAssignRepoMaintainer` delegation), but
 * only from the dashboard — an engineer never sees the tool at all. Same shape as
 * `cancel_access_request` exposing only the self-cancel half of its shared function:
 * the MCP surface is deliberately narrower than the UI, never wider.
 */
/**
 * NOTE the default at the bottom of mcpToolAllowed is `return true` — a tool NOT listed
 * here is callable by engineers. Anything dangerous must be added explicitly.
 * `plan_agent_command` / `draft_agent_command` render a diff and create a DRAFT; they can
 * never dispatch. There is deliberately **no approve_agent_command tool** — dispatching a
 * command into a live cluster stays a human action in the dashboard, exactly as
 * access-requests has no `decide` tool.
 */
/**
 * MCP tools gated to ROOT ALONE — stricter than MAINTAINER_MCP_TOOLS.
 *
 * `run_job` causes a scheduler job to EXECUTE. That is a different kind of act from
 * everything else on this surface: the rest of the MCP reads inventory or curates a
 * row, and its worst outcome is wrong metadata. A job run reaches outside this app —
 * `atlas-whitelist-reconcile` writes MongoDB Atlas access lists, `secret-inventory`
 * triggers a GitLab pipeline — and it is not undoable once dispatched.
 *
 * Root-only is deliberately NARROWER than the dashboard, which allows root|engineer
 * (`canWriteScheduler`, on the app role). Same principle as `update_repo` (whose Owner-delegation
 * half is dashboard-only) and `cancel_access_request` (self-cancel half only): the MCP
 * surface is narrower than the UI, never wider. A human clicking "Run now" has picked
 * that job on purpose; an agent may reach for one speculatively.
 */
export const ROOT_MCP_TOOLS = ['run_job'];

export const MAINTAINER_MCP_TOOLS = [
  'set_key_note',
  'update_repo',
  'bulk_assign_owner',
  'plan_agent_command',
  'draft_agent_command',
  'set_alert_selector',
];

/** Who may cause a scheduler job to execute. Root alone — see ROOT_MCP_TOOLS. */
export function canRunJobs(role: AppRole): boolean {
  return role === 'root';
}

/**
 * Scheduler WRITES from the dashboard — the enabled toggle, the schedule edit and
 * Run now (`setJobEnabled` / `updateJobSchedule` / `runJobNow`).
 *
 * Takes the infras-local app role (`app_roles`), like every other rule in this file
 * and like `@RequireRole`. It used to be read off the staff-auth JWT `roles` claim via
 * the legacy `isAdmin` flag, which is a DIFFERENT store: a user the app itself labels
 * `root` (badge, nav, secret levels, Atlas prune — all `app_roles`) was refused by the
 * scheduler because their SSO token carried no `root` group. One source, one answer.
 *
 * The bar is unchanged: root|engineer. Deliberately NOT maintainer — widening is a
 * separate decision, not a side effect of fixing which store is read.
 */
export const canWriteScheduler = (role: AppRole): boolean =>
  role === 'root' || role === 'engineer';

export function mcpToolAllowed(role: AppRole, tool: string): boolean {
  // Root-only is checked FIRST and returns unconditionally. Order matters: the final
  // `return true` below is a fail-OPEN default, so any tool that must be restricted has
  // to be caught by an explicit branch before reaching it.
  if (ROOT_MCP_TOOLS.includes(tool)) return role === 'root';
  if (MAINTAINER_MCP_TOOLS.includes(tool)) return role === 'root' || role === 'maintainer';
  if (role === 'new_member') return NEW_MEMBER_MCP_TOOLS.includes(tool);
  return true;
}
